/* eslint-disable no-undef */
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import {
  Box,
  Typography,
  IconButton,
  Popover,
  Grid,
  ClickAwayListener,
  Alert,
  Snackbar,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import "react-datepicker/dist/react-datepicker.css";
import "../../../../Asset/scss/Homepages/header.scss";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { ConfigProvider, DatePicker, Space } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/th";
import "dayjs/locale/en";
import locale from "antd/es/locale/th_TH";
import { useSelector, useDispatch } from "react-redux";
import {
  getMarinaDetail,
  getProvinceDetail,
  getDestinationDetail,
} from "../../../../redux/centerData/CenterDataSlice";

function SearchForm() {
  // Language selection and localization
  const { t } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem("selectedLanguage") || i18n.language
  );
  dayjs.locale(currentLanguage);

  // Retrieve stored destination from localStorage or set default values
  const storedDestination = JSON.parse(localStorage.getItem("Destination"));
  const initialDestination = storedDestination
    ? { ...storedDestination }
    : { name: "", eng: "", type: "" };

  const [destination, setDestination] = useState(initialDestination);

  // Retrieve stored trip type from localStorage or set default value
  const [tripType, setTripType] = useState(localStorage.getItem("Trip") || "");

  // Retrieve stored date from localStorage and parse it
  const [date, setDate] = useState(() => {
    const storedDate = localStorage.getItem("selectedDate");
    if (storedDate) {
      const dates = storedDate.split(",");
      if (dates.length === 1) {
        return dayjs(dates[0], "YYYY/MM/DD");
      } else if (dates.length === 2) {
        return [dayjs(dates[0], "YYYY/MM/DD"), dayjs(dates[1], "YYYY/MM/DD")];
      }
    }
    return null;
  });

  // Retrieve stored passenger count from localStorage or set default values
  const [counters, setCounters] = useState(() => {
    const storedCounters = JSON.parse(localStorage.getItem("Passenger"));
    return storedCounters || { adults: 1, children: 0, infants: 0 };
  });

  // Define passenger options
  const passengerOptions = [
    {
      key: "adults",
      labelKey: "adults",
      type: "adult",
      rangeKey: "adultsRange",
    },
    {
      key: "children",
      labelKey: "children",
      type: "child",
      rangeKey: "childrenRange",
    },
    {
      key: "infants",
      labelKey: "infants",
      type: "infant",
      rangeKey: "infantsRange",
    },
  ];

  // Ensure localStorage also defaults to at least 1 adult
  useEffect(() => {
    const storedCounters = JSON.parse(localStorage.getItem("Passenger"));
    if (!storedCounters || storedCounters.adults < 1) {
      setCounters((prevCounters) => ({ ...prevCounters, adults: 1 }));
    }
  }, []);

  // Update localStorage when passenger count changes
  useEffect(() => {
    localStorage.setItem("Passenger", JSON.stringify(counters));
  }, [counters]);

  // Redux state and data fetching
  const todos = useSelector((state) => state);
  const dispatch = useDispatch();

  // Loading state for data fetching
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Fetch data from API
    Promise.all([
      dispatch(getMarinaDetail({})),
      dispatch(getProvinceDetail({})),
      dispatch(getDestinationDetail({})),
    ])
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, [dispatch]);

  // Handle destination selection change
  const handleDestinationChange = (event, value) => {
    const destinationValue = value ? value : null;
    setDestination(destinationValue);
    localStorage.setItem("Destination", JSON.stringify(destinationValue));
  };

  // Extracting IDs from marina data
  const marinaIds = new Set(
    todos?.CenterDataSlice?.Marina?.results?.map((marina) => marina.id_province)
  );

  // Fetching results from Redux store
  const marinaResults = todos?.CenterDataSlice?.Marina?.results || [];
  const provinceResults = todos?.CenterDataSlice?.Province?.results || [];
  const destinationResults = todos?.CenterDataSlice?.Destination?.results || [];

  // Generate options list with translated names
  const generateOptions = (items, nameKeyThai, nameKeyEng, type) => {
    return items.map((item) => ({
      name: currentLanguage === "th" ? item[nameKeyThai] : item[nameKeyEng],
      eng: item[nameKeyEng],
      type: t(type),
    }));
  };

  // Filtering provinces linked to marina
  const provincesWithOptions = provinceResults.filter((province) =>
    marinaIds.has(province.ProvinceID)
  );
  const marinasWithOptions = marinaResults;
  const destinationsWithOptions = destinationResults;
  const optionsA = [
    ...generateOptions(
      provincesWithOptions,
      "ProvinceThai",
      "ProvinceEng",
      "province"
    ),
    ...generateOptions(
      marinasWithOptions,
      "name_marina_th",
      "name_marina_en",
      "marina"
    ),
    ...generateOptions(
      destinationsWithOptions,
      "name_destination_th",
      "name_destination_en",
      "destination"
    ),
  ];

  // Handle trip type change
  const handleTripTypeChange = (value) => {
    setTripType(value);
    setDate("");
    localStorage.setItem("Trip", value);
    localStorage.setItem("selectedDate", "");
  };

  // Handle date picker change and update localStorage
  const handleDatePickerChange = (selectedDates) => {
    setDate(selectedDates);
    if (!selectedDates) {
      setTripType("");
      localStorage.removeItem("Trip");
    }
    if (selectedDates === null) {
      localStorage.removeItem("selectedDate");
    } else if (Array.isArray(selectedDates)) {
      const dateString = selectedDates
        .map((date) => date.format("YYYY/MM/DD"))
        .join(",");
      localStorage.setItem("selectedDate", dateString);
    } else {
      localStorage.setItem("selectedDate", selectedDates.format("YYYY/MM/DD"));
    }
  };

  // Passenger popover state
  const [anchorEl, setAnchorEl] = useState(null);
  const boxRef = useRef(null);

  const handleOpenPopover = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  // Handle passenger count adjustments
  const handleCounterChange = (key, operation, event) => {
    event.stopPropagation();

    // ใช้ setCounters เพื่ออัพเดต counters
    setCounters((prevCounters) => {
      const newCounters = { ...prevCounters };

      // อัพเดตค่าตาม operation ที่รับมา
      if (operation === "increase") {
        newCounters[key] = (newCounters[key] || 0) + 1; // เพิ่มค่า
      } else if (operation === "decrease") {
        if (key === "adults" && newCounters[key] > 1) {
          newCounters[key]--; // ลดลงเฉพาะถ้าไม่เกิน 1
        } else if (key !== "adults" && newCounters[key] > 0) {
          newCounters[key]--; // ลดลงเฉพาะถ้าไม่ต่ำกว่า 0
        }
      }

      // เก็บค่าที่อัพเดตใน localStorage หลังจาก setCounters เรียบร้อยแล้ว
      localStorage.setItem("Passenger", JSON.stringify(newCounters));

      // คืนค่า newCounters กลับไปเพื่ออัพเดต state
      return newCounters;
    });
  };

  // Update language state when it changes
  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);
  useEffect(() => {
    const storedLanguage = localStorage.getItem("selectedLanguage");
    if (storedLanguage) {
      setCurrentLanguage(storedLanguage);
    }
  }, []);

  // Close passenger count dropdown when clicking outside
  const dropdownRef = useRef(null);
  const { RangePicker } = DatePicker;
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPassengerCount(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle form submission
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const handleButtonClick = () => {
    if (!destination || !destination.eng) {
      setAlertMessage(t("Please select a destination before booking."));
      setShowAlert(true);
      return;
    }

    if (!date) {
      setAlertMessage(t("Please select a date before booking."));
      setShowAlert(true);
      return;
    }

    let url = `/yacht/${destination.eng}`;

    if (tripType) {
      url += `/${tripType}`;
    }

    const totalPassengers =
      parseInt(counters.adults || 0, 10) +
      parseInt(counters.children || 0, 10) +
      parseInt(counters.infants || 0, 10);

    const dateParam = Array.isArray(date)
      ? date.map((d) => d.format("YYYY-MM-DD")).join("_")
      : date?.format("YYYY-MM-DD") || "";

    const queryParams = new URLSearchParams({
      passengers: totalPassengers,
      date: dateParam,
    });

    url += `?${queryParams.toString()}`;

    window.location.href = url;
  };

  // Function to check if an option is equal to a given value
  const isOptionEqualToValue = (option, value) => {
    return (
      option.name === value?.name &&
      option.eng === value?.eng &&
      option.type === value?.type
    );
  };

  // ป้องกันการเลือกวันที่ย้อนหลัง
  const disabledDate = (current) => {
    return current && current < dayjs().startOf("day");
  };

  return (
    <Box
      className="container-form-a"
      sx={{
        flexGrow: 1,
        width: "100%",
        maxWidth: "70%",
        zIndex: 5,
        "@media (max-width:600px)": {
          maxWidth: "80%",
        },
      }}
    >
      <Grid
        container
        spacing={0.5}
        rowGap={0.8}
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12} md={3}>
          <Autocomplete
            id="combo-box-demo"
            options={optionsA}
            value={destination || { name: "", eng: "", type: "" }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={isOptionEqualToValue}
            renderOption={(props, option) => (
              <li
                {...props}
                className="flex-between-center"
              >
                <Typography variant="body1">
                  {option.name}
                  <span className="text-muted-40">
                    &nbsp;&nbsp; - {option.type}
                  </span>
                </Typography>
              </li>
            )}
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              borderRadius: 8,
              "& .MuiOutlinedInput-root": {
                borderRadius: 8,
                height: "100%",
                width: "100%",
              },
              "@media (max-width:600px)": {
                height: "100%",
                width: "100%",
                marginBottom: 1,
                fontSize: "1rem",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 8,
                  height: "100%",
                  width: "100%",
                  fontSize: "1rem",
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  params.inputProps.value ||
                    params.inputProps.value === 0 ||
                    params.selectedItem
                    ? t("enterDestination")
                    : t("enterDestination")
                }
                sx={{
                  "& .MuiInputLabel-root": {
                    top: "50%",
                    transform: "translateY(-50%)",
                    color:
                      params.inputProps.value ||
                        params.inputProps.value === 0 ||
                        params.selectedItem
                        ? "white"
                        : "gray",
                    fontSize: "0.95rem",
                    transition: "all 0.2s ease",
                    marginLeft: "0.8rem",
                  },
                  "& .MuiInputLabel-shrink": {
                    top: 4,
                    transform: "translateY(0%) scale(0.75)",
                    color: "white",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 8,
                  },
                  "& label.Mui-focused": {
                    color: "white",
                    top: -16,
                  },
                  "& label": {
                    color:
                      params.inputProps.value ||
                        params.inputProps.value === 0 ||
                        params.selectedItem
                        ? "white"
                        : "gray",
                    top:
                      params.inputProps.value ||
                        params.inputProps.value === 0 ||
                        params.selectedItem
                        ? -8
                        : 0,
                  },
                }}
              />
            )}
            className="z-9999"
            onChange={handleDestinationChange}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select
            label={t("Select trip type")}
            variant="outlined"
            size="large"
            value={tripType}
            onChange={(e) => handleTripTypeChange(e.target.value)}
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              borderRadius: 8,
              "& .MuiOutlinedInput-root": {
                borderRadius: 8,
                width: "100%",
                height: "100%",
              },
              "& label": {
                color: tripType ? "white" : "gray",
                top: tripType ? -8 : "50%", // ปรับ label อยู่กลาง input
                transform: tripType ? "translateY(0)" : "translateY(-50%)", // center vertical
                transition: "all 0.2s ease", // เพิ่ม animation ลอย label
                marginLeft: "1rem",
              },
              "& label.Mui-focused": {
                fontSize: "11px",
                color: "white",
                top: -15,
                transform: "translateY(0)",
              },
              "@media (max-width:600px)": {
                maxWidth: "100%",
              },
            }}
          >
            <MenuItem value="HalfDay">{t("Half Day")}</MenuItem>
            <MenuItem value="DayTrip">{t("Full Day")}</MenuItem>
            <MenuItem value="Overnight">{t("Overnight")}</MenuItem>
          </TextField>
        </Grid>

        {tripType ? (
          <Grid item xs={12} sm={6} md={3}>
            <Space
              direction="vertical"
              className="ml-1 w-full"
            >
              {tripType ? (
                tripType === "HalfDay" || tripType === "DayTrip" ? (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ConfigProvider
                      locale={currentLanguage === "th" ? locale : "en"}
                    >
                      <DatePicker
                        className="custom-range-picker"
                        onChange={handleDatePickerChange}
                        size="15"
                        value={date}
                        picker="date"
                        format="YYYY/MM/DD"
                        disabled={!tripType}
                        disabledDate={disabledDate}
                      />
                    </ConfigProvider>
                  </LocalizationProvider>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ConfigProvider
                      locale={currentLanguage === "th" ? locale : "en"}
                    >
                      <RangePicker
                        className="custom-range-picker"
                        onChange={handleDatePickerChange}
                        size="15"
                        value={date}
                        picker="date"
                        format="YYYY/MM/DD"
                        disabled={!tripType}
                        disabledDate={disabledDate}
                      />
                    </ConfigProvider>
                  </LocalizationProvider>
                )
              ) : null}
            </Space>
          </Grid>
        ) : null}

        <ClickAwayListener onClickAway={handleClosePopover}>
          <Grid item xs={12} sm={6} md={2}>
            <Box
              ref={boxRef}
              className="passenger-box"
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "3vw",
                backgroundColor: "white",
                borderRadius: 8,
                cursor: "pointer",
                justifyContent: "space-between",
                "@media (max-width:600px)": {
                  height: "8vh",
                  width: "100%",
                },
              }}
              onClick={handleOpenPopover}
            >
              <Grid
                container
                spacing={0}
                justifyContent="space-between"
                sx={{ px: 2 }}
              >
                {passengerOptions.map((option) => (
                  <Grid item xs={2} md={3} key={option.key}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                        "@media (max-width:600px)": {
                          height: "10vw",
                          width: "100%",
                        },
                      }}
                    >
                      <Typography
                        variant=""
                        sx={{
                          fontWeight: "bold",
                          marginLeft: "2vw",
                          marginTop: "3px",
                        }}
                        className="ml-1vw"
                      >
                        {counters[option.key]}
                      </Typography>
                      <Typography
                        variant=""
                        align="center"
                        sx={{
                          fontSize: "0.7vw",
                          marginLeft: "2vw",
                          "@media (max-width:600px)": {
                            fontSize: "0.8rem",
                            height: "12vw",
                            width: "100%",
                          },
                        }}
                        className="ml-1vw"
                      >
                        {t(option.labelKey)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
                <Grid
                  item
                  md={1}
                  sx={{
                    marginLeft: "0.5vw",
                    marginTop: "0.5vw",
                    alignItems: "center",
                    "@media (max-width:600px)": {
                      marginTop: "2vw",
                    },
                  }}
                >
                  {anchorEl ? (
                    <ArrowDropUpIcon fontSize="medium" />
                  ) : (
                    <ArrowDropDownIcon fontSize="medium" />
                  )}
                </Grid>
              </Grid>

              <Popover
                open={Boolean(anchorEl)}
                anchorEl={boxRef.current}
                onClose={handleClosePopover}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box p={2}>
                  {passengerOptions.map((option) => (
                    <div
                      key={option.key}
                      className="flex-between-center mb-10"
                    >
                      <div className="text-left">
                        <Typography variant="" sx={{ fontWeight: "bold" }}>
                          {t(option.labelKey)}
                        </Typography>
                        <Typography variant="body2">
                          {t(option.rangeKey)}
                        </Typography>
                      </div>
                      <div className="flex-center2">
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleCounterChange(option.key, "decrease", event)
                          }
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography>{counters[option.key] || 0}</Typography>
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleCounterChange(option.key, "increase", event)
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      </div>
                    </div>
                  ))}
                </Box>
              </Popover>
            </Box>
          </Grid>
        </ClickAwayListener>

        <Grid item xs={12} sm={6} md={2}>
          <Button
            variant="contained"
            endIcon={<SearchIcon sx={{ color: "white" }} />}
            sx={{
              height: "6vh",
              width: "100%",
              backgroundColor: "#00305b",
              borderRadius: 8,
              "& .MuiOutlinedInput-root": {
                borderRadius: 8,
                fontSize: "1rem",
              },
              "@media (max-width:600px)": {
                height: "8vh",
                width: "100%",
                fontSize: "0.5rem",
              },
            }}
            onClick={handleButtonClick}
          >
            {t("letsGoButton")}
          </Button>
          {/* </Link> */}
        </Grid>
      </Grid>

      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          sx={{
            borderRadius: 8,
            "@media (max-width:600px)": {
              height: "50%",
              width: "100%",
              fontSize: "0.6rem",
              "& .MuiOutlinedInput-root": {
                borderRadius: 8,
                height: "60%",
                width: "100%",
                fontSize: "0.6rem",
              },
            },
          }}
          elevation={6}
          variant="filled"
          severity="warning"
          onClose={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SearchForm;
